import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../../../providers/theme-provider";
import { assetIcons } from "../../../constants/assets";
import CtaButton from "../../buttons/cta-button/cta-button";
import useSmoothScroll from "../../../hooks/use-smooth-scroll";
import { useTranslation } from "react-i18next";
import { fontSizes } from "../../../theme/font-sizes";
import useWindowSize from "../../../hooks/use-window-size";

const FloatingNavbar = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { colors, isDarkMode, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const handleScroll = useSmoothScroll();
  const { width } = useWindowSize();
  const isMobile = width < 768;

  const navItems = [
    {
      label: t("navbar.navItems.home"),
      sectionId: "home",
      smallSectionId: "home",
    },
    {
      label: t("navbar.navItems.services"),
      sectionId: "services",
      smallSectionId: "services",
    },
    {
      label: t("navbar.navItems.testimonials"),
      sectionId: "testimonials",
      smallSectionId: "testimonials-small",
    },
    {
      label: t("navbar.navItems.contact"),
      sectionId: "contact",
      smallSectionId: "contact-small",
    },
  ];
  useEffect(() => {
    const handleScrollVisibility = () => {
      const scrollPosition = window.scrollY;
      const showThreshold = 200; // Adjust as needed
      setIsVisible(scrollPosition > showThreshold);
    };

    window.addEventListener("scroll", handleScrollVisibility);
    return () => window.removeEventListener("scroll", handleScrollVisibility);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const navbarVariants = {
    hidden: { y: -100, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      y: -100,
      opacity: 0,
      transition: {
        duration: 0.2,
        ease: "easeIn",
      },
    },
  };

  // Determine the appropriate image for the theme toggle button
  const themeToggleIcon = isDarkMode
    ? assetIcons.bulbDark
    : assetIcons.bulbLight;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed top-4 left-0 right-0 lg:w-[80%] w-[90%] z-50 shadow-lg mx-auto"
          variants={navbarVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <div
            className="backdrop-blur-md bg-white/80 rounded-lg"
            style={{
              backgroundColor: colors.heroBg + "80",
            }}
          >
            {!isMobile ? (
              // Large Screen Navbar
              <div className="max-w-[95%] mx-auto py-4 flex justify-between items-center">
                <img
                  src="/logo.png"
                  alt=""
                  className="cursor-pointer lg:w-auto w-32"
                  onClick={() => handleScroll("home")}
                />

                <nav>
                  <ul className="flex lg:gap-8 gap-5 items-center">
                    {navItems.map((item, index) => (
                      <li
                        key={index}
                        className="font-medium cursor-pointer relative group"
                        style={{
                          color: colors.textColor,
                          fontSize: fontSizes.body,
                        }}
                        onClick={() => handleScroll(item.sectionId)}
                      >
                        {item.label}
                        <motion.div
                          className="absolute left-0 bottom-0 h-[2px] w-full scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                          style={{ backgroundColor: colors.redColor }}
                        />
                      </li>
                    ))}
                  </ul>
                </nav>

                <div className="flex items-center gap-2">
                  <div
                    className="xl:p-4 p-3 rounded-sm cursor-pointer"
                    style={{
                      backgroundColor: colors.themeToggle,
                    }}
                    onClick={toggleTheme}
                  >
                    <img src={themeToggleIcon} alt="" />
                  </div>
                  {/* <CtaButton
                    label={t("navbar.ctaButton")}
                    onClick={() => handleScroll("contact")}
                  /> */}
                  <button
                    className="xl:p-4 p-3 rounded-sm"
                    style={{
                      backgroundColor: colors.primary,
                    }}
                    onClick={() => handleScroll("contact")}
                  >
                    <img src={assetIcons.send} alt="" />
                  </button>
                </div>
              </div>
            ) : (
              // Mobile Screen Navbar
              <div className="p-4 flex justify-between items-center">
                <img
                  src="/logo.png"
                  alt=""
                  className="cursor-pointer w-32"
                  onClick={() => handleScroll("home")}
                />
                <button onClick={() => setIsMenuOpen(true)}>
                  <img src={assetIcons.menu} alt="" className="w-6" />
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                className="fixed inset-0 bg-white/90 backdrop-blur-[4px] z-50 flex flex-col px-6 py-4 h-[60vh] shadow-md"
                initial={{ opacity: 0 }}
                animate={{ opacity: 100 }}
                exit={{ opacity: 0 }}
                transition={{ type: "tween", duration: 0.3, ease: "easeInOut" }}
              >
                <div className="flex justify-between items-center mb-8 relative z-50">
                  <img src="/logo.png" alt="Logo" className="w-32" />
                  <button onClick={toggleMenu} className="p-2">
                    <img src={assetIcons.cross} alt="" className="w-6" />
                  </button>
                </div>

                <nav className="flex flex-col gap-6 text-lg relative z-50">
                  {navItems.map((item, index) => (
                    <li
                      className="hover:opacity-70 transition-opacity list-none"
                      style={{
                        fontSize: fontSizes.body,
                      }}
                      onClick={() => {
                        handleScroll(item.smallSectionId);
                        toggleMenu();
                      }}
                    >
                      {item.label}
                    </li>
                  ))}
                </nav>

                <div className="flex gap-2 mt-10">
                  <CtaButton
                    label={t("navbar.ctaButton")}
                    onClick={() => {
                      handleScroll("contact-small");
                      setIsMenuOpen(false);
                    }}
                    style={{
                      marginTop: "auto",
                      marginBottom: 32,
                      width: "60%",
                      paddingTop: "14px",
                      paddingBottom: "14px",
                    }}
                  />
                  <div
                    className="xl:p-4 p-4 rounded-sm cursor-pointer mt-auto mb-8"
                    style={{
                      backgroundColor: colors.themeToggle,
                    }}
                    onClick={toggleTheme}
                  >
                    <img src={themeToggleIcon} alt="" />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingNavbar;
